import { Injectable, inject } from "@angular/core";
import { Questionnaire } from "../classes/flow/Questionnaire/Questionnaire";
import { Specialty } from "../classes/flow/Questionnaire/Specialty";
import { EpfService } from "./epf.service";

export type UpdateKey = "title" | "description" | "questionnaireTypeId" | "active";

@Injectable({
  providedIn: "root",
})
export class QuestionnaireService {
  private epf = inject(EpfService);

  /**
   * Updates a single property of the questionnaire matching the given id
   * @param questionnaire The questionnaire to update
   * @param key The property of the questionnaire to update
   * @param value The new value for the given property
   */
  public async updateProperty(questionnaire: Questionnaire, key: UpdateKey, value: string | boolean | number) {
    const payload: Questionnaire = { ...questionnaire };
    payload[key] = value;

    await this.epf.updateQuestionnaire(payload);

    questionnaire.changes = payload.changes;
  }

  /**
   * Sets the specialties for the questionnaire matching the given id
   * @param questionnaireId The id of the questionnaire to change
   * @param specialties A collection of the new specialties for the questionnaire
   */
  public async updateSpecialties(questionnaireId: number, specialties: Specialty[]) {
    await this.epf.deleteAllSpecialtyQuestionnaireByQuestionnaire(questionnaireId);

    await Promise.all(
      specialties
        .map((specialty) => {
          return { specialtyId: specialty.id, questionnaireId: questionnaireId };
        })
        .map((x) => {
          return this.epf.addSpecialtyQuestionnaire(x.questionnaireId, x.specialtyId);
        }),
    );
  }

  /**
   * Delete a questionnaire
   * @param questionnaire The questionnaire to delete
   */
  public async deleteQuestionnaire(questionnaire: Questionnaire) {
    return this.updateProperty(questionnaire, "active", false);
  }

  public async getPdfFile(questionnaireId: number): Promise<string> {
    const result = await this.epf.getQuestionnaireAsPDF(questionnaireId);

    return result.content;
  }

  public async copyQuestionnaire(questionnaire: Questionnaire) {
    return this.epf.copyQuestionnaire(questionnaire);
  }

  public async setQuestionnaireSortOrder(questionnaire: Questionnaire): Promise<void> {
    return this.epf.updateQuestionnaire(questionnaire);
  }
}
